import { IPROP_MAP, modifyProps } from '../../generators/modifyProps';
import { forwardRef, useMemo } from 'react';

import { ButtonProps } from '../jdsbutton/logic';
import { JDSLinkProps } from './generated';
import { TextProps } from '../jdstext/logic';
import { convertTextToComponent } from '../../utils/componenthelpers';
import { withProviderProps } from './provider.modifier';

const legacyKinds = ['rich-text'] as const;
type legacyKindsType = (typeof legacyKinds)[number];

export interface LinkProps
  extends Omit<
    JDSLinkProps,
    'kind' | 'onClick' | 'newTab' | 'textAppearance' | 'onHoverEnd' | 'onHover'
  > {
  kind?: JDSLinkProps['kind'] | legacyKindsType;
  children?: React.ReactNode;
  newTab?: boolean;
  onClick?: (ev: React.MouseEvent<HTMLDivElement>) => void;
  textAppearance?: TextProps['appearance'] | 'none';
  provider?: JSX.Element;
  target?: string;
  /**
   * @deprecated
   */
  _button?: ButtonProps;
}

export const PROP_MAP: IPROP_MAP<LinkProps> = (props) => {
  if (props.kind === 'rich-text') {
    console.warn('rich-text is deprecated, use rich_text instead');
  }
  return {
    newTab: {
      properties: {
        newTab: props.newTab ? '_blank' : undefined
      }
    },
    kind: {
      variations: {
        default: {
          kind: 'default'
        },
        'rich-text': {
          kind: 'rich_text'
        }
      }
    },
    textAppearance: {
      properties: {},
      variations: {
        'body-s': { textAppearance: 'body-s' },
        'heading-xl': { textAppearance: 'heading_xl' },
        'heading-l': { textAppearance: 'heading_l' },
        'heading-m': { textAppearance: 'heading_m' },
        'heading-s': { textAppearance: 'heading_s' },
        'heading-xs': { textAppearance: 'heading_xs' },
        'heading-xxs': { textAppearance: 'heading_xxs' },
        overline: { textAppearance: 'overline' },
        'body-l': { textAppearance: 'body_l' },
        'body-l-bold': { textAppearance: 'body_l_bold' },
        'body-l-link': { textAppearance: 'body_l_link' },
        'body-m': { textAppearance: 'body_m' },
        'body-m-bold': { textAppearance: 'body_m_bold' },
        'body-m-link': { textAppearance: 'body_m_link' },
        'body-s-bold': { textAppearance: 'body_s_bold' },
        'body-s-link': { textAppearance: 'body_s_link' },
        'body-xs': { textAppearance: 'body_xs' },
        'body-xs-bold': { textAppearance: 'body_xs_bold' },
        'body-xs-link': { textAppearance: 'body_xs_link' },
        'body-xxs': { textAppearance: 'body_xxs' },
        'body-xxs-bold': { textAppearance: 'body_xxs-bold' },
        'body-xxs-link': { textAppearance: 'body_xxs_link' },
        'list-title': { textAppearance: 'list_title' },
        button: { textAppearance: 'button' },
        code: { textAppearance: 'code' }
      }
    }
  };
};

export const withLogic = (
  Component: React.ComponentType<LinkProps>
): React.FC<LinkProps> =>
  forwardRef<HTMLDivElement, LinkProps>((userProps: LinkProps, ref) => {
    const props = modifyProps(userProps, PROP_MAP);
    const ComponentWithProvider = useMemo(
      () => withProviderProps(Component),
      []
    );

    return (
      <ComponentWithProvider
        {...{
          ...props,
          rootRef: ref,
          textAppearance: props.textAppearance || 'body_s',
          _hasText: Boolean(props.title),
          _hasChildren: Boolean(props.children) && !props.title,
          children: convertTextToComponent(props.children)
        }}
      />
    );
  });
